<template>
  <widget-container :widget="widget">
    <v-toolbar
      dense
      flat
      elevation="0"
      style="max-height: 10%; height: 10%"
      class="d-flex justify-end"
      v-if="!shouldFake"
    >
      <div class="mr-2" v-if="!shouldUseDashboardGlobalTimerange">
        <chart-time-range-form v-model="dateRange" />
      </div>

      <div v-if="!loading && hasData(opts?.series)">
        <chart-aggregation-form
          class="mr-2"
          :series="opts.series"
          v-model="aggregationSettings"
          @change="updateAggregation"
        />

        <widget-settings-menu
          v-if="!loading && !hideChartExport"
          :menu-options="settingsOptionsList"
          @export="handleExport"
        />
      </div>
    </v-toolbar>

    <v-row
      no-gutters
      :style="`display:flex;flex-wrap:nowrap;height: 90%; ${
        hasTitle ? 'padding-bottom: 4.5rem;' : 'padding-top: 1.5rem;'
      }`"
    >
      <div style="height: 100%" v-if="displaySideLegend && !shouldFake">
        <div
          style="overflow-y: scroll; overflow-x: hidden; height: 100%"
          class="hide-scrollbar"
        >
          <chart-legend
            :series="opts?.series"
            :series-data="seriesData"
            :display-metas="chartLegendMetas"
            :zoom-procent="dataZoomProcent"
            :date-range="dateRange"
          />
        </div>
      </div>

      <e-chart
        v-if="!loading && hasData(opts?.series)"
        ref="echart"
        :option="opts"
        @dataZoom="updateDataZoom"
        style="width: 100%; max-width: 100%; min-height: 100%; max-height: 100%"
        :autoresize="true"
      />
      <h3
        v-else-if="!loading && !hasData(opts?.series)"
        style="width: 100%; text-align: center; align-self: center"
      >
        {{ $t("module.noData") }}
      </h3>
      <v-progress-circular
        v-else
        :size="50"
        :width="5"
        indeterminate
        color="primary"
        style="margin: auto"
      />
    </v-row>
  </widget-container>
</template>

<script>
import { format } from "date-fns";
import WidgetContainer from "./WidgetContainer.vue";
import "echarts/lib/coord/cartesian/Grid";
import { chartMixin } from "../../../_helpers/chartMixin";
import widgetMixin from "../../../_helpers/widgetMixin";
import exportMixin from "@/_helpers/exportMixin";
import tagDataRepository from "../../../api/repositories/tagDataRepository";
import MetricHelper from "@/_helpers/metricHelper.js";
import { mapState, mapMutations } from "vuex";
import { cloneDeep, isEqual } from "lodash-es";
import ChartLegend from "./ChartLegend.vue";
import ChartAggregationForm from "./ChartAggregationForm.vue";
import ChartTimeRangeForm from "./ChartTimeRangeForm.vue";
import WidgetSettingsMenu from "./WidgetSettingsMenu.vue";
import { aggregateWindows } from "@/_helpers/aggregationHelper";

export default {
  name: "TemplateChart",

  props: {
    widget: {
      default: undefined,
    },

    tags: {
      default: () => [],
    },

    notTemplate: {
      type: Boolean,
      default: false,
    },

    useTagNameAsSeriesName: {
      type: Boolean,
      default: false,
    },

    aggregationSettingsInput: {
      default: undefined,
    },

    dateRangeInput: {
      default: undefined,
    },
  },

  components: {
    WidgetContainer,
    ChartAggregationForm,
    ChartTimeRangeForm,
    ChartLegend,
    WidgetSettingsMenu,
  },

  mixins: [chartMixin, widgetMixin, exportMixin],

  data() {
    return {
      loading: false,
      dateRange: undefined,
      opts: undefined,
      seriesData: {},
      widgetClone: undefined,
      allowedAxisNameLength: 24,

      debounceTimer: null,
      aggregationSettings: {},
      chartLegendMetas: {},
      dataZoomProcent: {
        start: 0,
        end: 100,
      },
    };
  },

  computed: {
    ...mapState("tag", ["tagsWithMeta"]),
    ...mapState("templateDashboard", ["globalValues"]),

    dashboardGlobalTime() {
      return this.globalValues?.globalTimeRange;
    },

    dashboardGlobalZoom() {
      return this.globalValues?.dashboardZoom;
    },

    cmpTextColor() {
      return this.contentTextColor?.value ?? this.defaultTextColor;
    },

    displaySideLegend() {
      return Object.values(this.chartLegendMetas).some((x) => x?.length > 0);
    },
  },

  methods: {
    ...mapMutations("templateDashboard", ["SET_DASHBOARD_ZOOM"]),

    handleExport(value) {
      switch (value) {
        case this.widgetExportTypes.PNG:
          this.exportWidgetEchartAsPng(this.widget.widgetType);
          break;
        case this.widgetExportTypes.CSV:
          this.exportWidgetAsCSV(this.getZoomedData(), this.widget.widgetType);
          break;
        case this.widgetExportTypes.JSON:
          this.exportWidgetAsJson(this.getZoomedData(), this.widget.widgetType);
          break;
        default:
          console.error("Unsupported export type for Chart");
          return;
      }
    },

    updateAggregation(v) {
      this.aggregationSettings = v;
      this.$emit("update-aggregationSettings", v);
      this.connectDataToSeries();
    },

    getSeriesName(slot) {
      if (this.notTemplate && !this.useTagNameAsSeriesName)
        return this.widget?.name ?? "N/A";

      let slotName = `Node Slot ${slot.index}`;
      if (this.shouldFake || this.tags.length < slot.index - 1)
        return `${slotName} - ${slot.key}`;

      let tag = this.tags[slot.index - 1];
      if (tag === undefined) return `${slotName} - ${slot.key}`;

      return this.constructLabelFromTagAndKey(tag, slot.key);
    },

    setupOptions() {
      // Fetch the meta that are associated with this chart
      if (this.widget.slots === undefined) return undefined;

      let base = this.base;
      const chartLegendMetas = {};
      for (var slt of this.widget.slots) {
        let name = this.getSeriesName(slt);
        this.setupLegendAndSeries(base, name, slt.slotId);

        // Check if the series should have its own axis with the so_own_axis meta
        // If this is not true we use the default axis SYNC_Y is no longer valid
        const ownAxisMeta = this.getMeta(this.metaKeys.OWN_AXIS, slt.slotId);
        const maxMeta = this.getMeta(this.metaKeys.MAX, slt.slotId);
        const minMeta = this.getMeta(this.metaKeys.MIN, slt.slotId);
        if (
          ownAxisMeta !== undefined &&
          (ownAxisMeta.value === "true" || ownAxisMeta.value === true)
        ) {
          // Validate that yAxis should actually be pushed or if the yAxis already has the
          // name we should not do anything
          if (base.yAxis === undefined || base.yAxis.length <= 0)
            base.yAxis = [];

          // Find if there already a yAxis with the id of the slot
          if (base.yAxis.map((d) => d.slotId).includes(slt.slotId)) return;

          base.yAxis.push(
            this.yAxisBaseSync(
              slt.slotId,
              this.getAxisName(slt.slotId, name),
              minMeta === undefined ? undefined : parseFloat(minMeta.value),
              maxMeta === undefined ? undefined : parseFloat(maxMeta.value),
              base.yAxis.length,
              this.getMetaValueAsBoolWithSlot(
                this.metaKeys.CHART_INVERSE_Y_AXIS,
                slt.slotId
              )
            )
          );

          // Make space for new scale
          if (this.isVertical) {
            base.grid.top += 10 * base.yAxis.length;
            base.grid.right = 110;
          } else {
            base.grid.right += 10 * base.yAxis.length;
          }
          base.series[base.series.length - 1].yAxisIndex =
            base.yAxis.length - 1;
        } else {
          // Find the default y-axis
          let hasDefault = base.yAxis.findIndex((d) => d.default === true);
          if (hasDefault === -1) {
            base.yAxis.push(
              this.yAxisBaseDefault(
                slt.slotId,
                this.getAxisName(slt.slotId, name),
                minMeta === undefined ? undefined : parseFloat(minMeta.value),
                maxMeta === undefined ? undefined : parseFloat(maxMeta.value)
              )
            );

            base.series[base.series.length - 1].yAxisIndex =
              base.yAxis.length - 1;
          } else {
            let baseYAxis = base.yAxis[hasDefault];
            baseYAxis.name += ", " + this.getAxisName(slt.slotId, name);
            if (baseYAxis.name.length > this.allowedAxisNameLength)
              baseYAxis.name =
                baseYAxis.name.substring(0, this.allowedAxisNameLength) + "...";
            baseYAxis.slotIds.push(slt.slotId);
            base.series[base.series.length - 1].yAxisIndex = hasDefault;
          }
        }

        chartLegendMetas[slt.slotId] = [];
        if (
          this.getMetaValueAsBoolWithSlot(
            this.metaKeys.CHART_INTEGRAL,
            slt.slotId
          )
        )
          chartLegendMetas[slt.slotId].push({
            key: this.metaKeys.CHART_INTEGRAL,
            type: "integral",
          });
        if (
          this.getMetaValueAsBoolWithSlot(
            this.metaKeys.CHART_MINIMUM,
            slt.slotId
          )
        )
          chartLegendMetas[slt.slotId].push({
            key: this.metaKeys.CHART_MINIMUM,
            type: "min",
          });
        if (
          this.getMetaValueAsBoolWithSlot(
            this.metaKeys.CHART_MAXIMUM,
            slt.slotId
          )
        )
          chartLegendMetas[slt.slotId].push({
            key: this.metaKeys.CHART_MAXIMUM,
            type: "max",
          });
        if (
          this.getMetaValueAsBoolWithSlot(
            this.metaKeys.CHART_AVERAGE,
            slt.slotId
          )
        )
          chartLegendMetas[slt.slotId].push({
            key: this.metaKeys.CHART_AVERAGE,
            type: "avg",
          });
        if (
          this.getMetaValueAsBoolWithSlot(this.metaKeys.CHART_SUM, slt.slotId)
        )
          chartLegendMetas[slt.slotId].push({
            key: this.metaKeys.CHART_SUM,
            type: "sum",
          });
      }
      // Set chartLegendMetas for reactivity instead of modifying directly
      this.chartLegendMetas = chartLegendMetas;

      // Set the colors for each series
      base = this.setColors(base);

      if (this.isVertical) base = this.verticalAxis(base);

      let target = this.getMeta(this.metaKeys.TARGET);
      if (
        target !== undefined &&
        target.content != null &&
        target.content !== ""
      )
        base = this.setTargetSeries(base, target.content);

      this.opts = base;
    },

    setDefaultSeriesAggregation(series) {
      if (series.length === 0) return [];

      let temp = {};
      for (let s of series) {
        if (s.slotId === undefined) continue;

        temp[s.slotId] = {
          types: [
            this.getMeta(this.metaKeys.CHART_DEFAULT_AGGREGATION, s.slotId)
              ?.value ?? "last",
          ],
          windowSize: "auto",
        };
      }

      return temp;
    },

    setupLegendAndSeries(base, name, slotId) {
      this.addLegend(base, name);
      let serie = base.series.find((d) => d.slotId === slotId);
      if (serie) {
        serie.name = name;
        return;
      }

      switch (this.chartType(slotId)) {
        case "line":
          base.series.push(this.baseSeriesLine(slotId, name));
          break;
        case "area":
          base.series.push(this.baseSeriesArea(slotId, name));
          break;
        case "bar":
          base.series.push(this.baseSeriesBar(slotId, name));
          break;
        case "vertical":
          base.series.push(this.baseSeriesArea(slotId, name));
          break;
        case "scatter":
          base.series.push(this.baseSeriesScatter(slotId, name));
          break;
        case "trend":
          base.series.push(this.baseSeriesBar(slotId, name));
          break;
        default:
          base.series.push(this.baseSeriesArea(slotId, name));
          break;
      }
    },

    getAxisName(slotId, serieName) {
      const axisLabel = this.getMeta(this.metaKeys.AXIS_LABEL, slotId);
      return axisLabel === undefined
        ? serieName.split(" - ").pop()
        : axisLabel.value;
    },

    autoSetMaxMin(slotId) {
      let series =
        this.opts.series?.find((d) => d.slotId === slotId) ?? undefined;
      if (series === undefined) return;

      var axis = undefined;
      if (!this.isVertical)
        axis = this.opts.yAxis.find((d) => d.slotId === slotId);
      else axis = this.opts.xAxis.find((d) => d.slotId === slotId);

      if (axis === undefined && !this.isVertical)
        axis = this.opts.yAxis.find((d) => d.default);
      else if (axis === undefined)
        axis = this.opts.xAxis.find((d) => d.default);
      if (axis === undefined) return;

      // This part checks if the slot has max or min values.
      // If any we return after this and we do not need to
      // calculate the max and minimum value
      let hasValues = false;
      const maxMeta = this.getMeta(this.metaKeys.MAX, slotId);
      if (maxMeta !== undefined) {
        axis.max = parseFloat(maxMeta.value);
        hasValues = true;
      }

      const minMeta = this.getMeta(this.metaKeys.MIN, slotId);
      if (minMeta !== undefined) {
        axis.min = parseFloat(minMeta.value);
        hasValues = true;
      }

      if (hasValues || series.data.length == 0) return;

      var data = series.data.map((d) =>
        parseFloat(this.isVertical ? d.value[0] : d.value[1])
      );

      const max = Math.max(...data);
      const min = Math.min(...data);
      const maxPadded = max + (max - min) * 0.05;
      const minPadded = min - (max - min) * 0.05;

      if (axis.max === undefined || axis.max < Math.ceil(maxPadded))
        axis.max = Math.ceil(maxPadded);
      if (axis.min === undefined || axis.min > Math.floor(minPadded))
        axis.min = Math.floor(minPadded);
    },

    setColors(options) {
      for (var series of options.series) {
        if (series.slotId === undefined) {
          console.error("Could not find slot in series");
          continue;
        }

        series.showSymbol = !this.getMetaValueAsBoolWithSlot(
          this.metaKeys.HIDE_BULLETS,
          series.slotId
        );

        series.smooth = this.getMetaValueAsBoolWithSlot(
          this.metaKeys.CHART_SMOOTH,
          series.slotId
        );

        let color = this.getMeta(
          this.templateColorKeys.PER_SLOT,
          series.slotId
        );
        if (color === undefined) continue;

        series.itemStyle.color = color.value;
        if (this.chartType(series.slotId) == "area") {
          series = this.setAreaColor(series, color.value);
        }
      }

      // Fonts and stuff
      options.xAxis.splitLine.color = this.cmpTextColor;
      for (var ax of options.yAxis) {
        ax.axisLabel.color = this.cmpTextColor;
        ax.axisLine.lineStyle.color = this.cmpTextColor;
      }

      if (options.xAxis.length !== undefined)
        for (var ax of options.xAxis) {
          ax.axisLabel.color = this.cmpTextColor;
          ax.axisLine.lineStyle.color = this.cmpTextColor;
        }
      else {
        options.xAxis.axisLabel.color = this.cmpTextColor;
        options.xAxis.axisLine.lineStyle.color = this.cmpTextColor;
      }

      options.legend.textStyle = { color: this.cmpTextColor };

      return options;
    },

    fakeData() {
      let i = 0;
      for (var slt of this.widget.slots) {
        this.seriesData[slt.slotId] = this.generateFakeData(5 + i * 5);
        i++;
      }
    },

    async fetchData() {
      this.loading = true;
      const fetchedData = {};
      if (
        this.dateRange?.start === undefined ||
        this.dateRange?.end === undefined
      ) {
        for (var slt of this.widget.slots) fetchedData[slt.slotId] = [];
        this.seriesData = fetchedData;
        this.loading = false;
        return;
      }

      const range = this.getTimeRangeAsDates(this.dateRange);
      for (var slt of this.widget.slots) {
        // Api request to fetch data
        let tagId = this.tags[slt.index - 1];
        let key = slt.key;

        if (this.chartType(slt.slotId) === "trend")
          try {
            let response = await tagDataRepository.getTrendDataForTag(
              tagId,
              encodeURIComponent(key),
              MetricHelper.getMetricsUrlParams({
                from: this.toApiDate(range.start),
                to: this.toApiDate(range.end),
              })
            );

            fetchedData[slt.slotId] = response.data.map((e) => {
              return {
                name: e.date,
                value: [new Date(e.date), e.value],
              };
            });
          } catch {
            console.error(
              `Could not get trend data for tag: ${tagId} with key ${key}`
            );
            fetchedData[slt.slotId] = [];
            continue;
          }
        else
          try {
            if (
              tagId == undefined ||
              tagId == "" ||
              key == undefined ||
              key == ""
            )
              continue;

            let response = await tagDataRepository.getTimeSeriesTagDataSingle(
              tagId,
              key,
              this.toApiDate(range.start, true),
              this.toApiDate(range.end, true)
            );

            fetchedData[slt.slotId] = response.data.map((e) => {
              return {
                name: e.date,
                value: this.isVertical
                  ? [e.value, new Date(e.date)]
                  : [new Date(e.date), e.value],
              };
            });
          } catch {
            console.error(`Could not fetch data for ${tagId} from api`);
            fetchedData[slt.slotId] = [];
            continue;
          }
      }
      // Set seriesData for reactivity instead of modifying directly on slotId
      this.seriesData = fetchedData;
      this.loading = false;
    },

    connectDataToSeries() {
      this.resetAxisesMinMax();
      for (var slt of this.widget.slots) {
        var series =
          this.opts.series?.find((d) => d.slotId == slt.slotId) ?? undefined;
        if (series === undefined) continue;

        // Aggregation
        const aggregationFunction = this.aggregationSettings[slt.slotId]
          ?.types ?? ["last"];
        const aggregated = aggregateWindows(
          this.seriesData[slt.slotId]?.map((d) => d.value) ?? [],
          this.aggregationSettings[slt.slotId]?.windowSize ?? "auto",
          aggregationFunction,
          {
            fillEmpty: false,
            fillWith: undefined,
            invertedDataObject: this.isVertical,
          }
        );
        if (aggregated && aggregated.length > 0) {
          const resultingData = aggregated.map((d) => ({
            name: format(d.windowEnd, "yyyy-MM-dd HH:mm:ss"),
            value: this.isVertical
              ? [d[aggregationFunction], d.windowEnd]
              : [d.windowEnd, d[aggregationFunction]],
          }));

          series.data = resultingData;
        } else series.data = this.seriesData[slt.slotId] ?? [];

        this.autoSetMaxMin(slt.slotId);
      }

      if (!this.isVertical && this.opts?.xAxis !== undefined) {
        let range = this.getTimeRangeAsDates(this.dateRange);
        this.opts.xAxis = this.setAxisDateRange(
          this.opts.xAxis,
          range.start,
          range.end
        );
      }

      if (this.isVertical && this.opts?.yAxis !== undefined) {
        let range = this.getTimeRangeAsDates(this.dateRange);
        this.opts.yAxis = this.setAxisDateRange(
          this.opts.yAxis,
          range.start,
          range.end
        );
      }

      clearTimeout(this.debounceTimer);
      this.$emit("updated-chart-data", this.opts.series);
    },

    resetAxisesMinMax() {
      var axises = this.isVertical ? this.opts.xAxis : this.opts.yAxis;

      for (var axis of axises) {
        //Sets min/max to undefined. Will be recalculated later in autoSetMaxMin
        axis.min = undefined;
        axis.max = undefined;
      }
    },

    isNewKeys() {
      for (var slt of this.widget.slots) {
        let newName = this.getSeriesName(slt);
        let series =
          this.opts?.series?.find((d) => d.slotId == slt.slotId) ?? undefined;
        if (series?.name != newName) return true;
      }
      return false;
    },

    replaceLegend() {
      if (this.widget.slots === undefined) return undefined;

      let modifiedOpts = cloneDeep(this.opts);
      this.clearLegend(modifiedOpts);

      for (var slt of this.widget.slots) {
        this.setupLegendAndSeries(
          modifiedOpts,
          this.getSeriesName(slt),
          slt.slotId
        );
      }
      this.opts = modifiedOpts;
    },

    replaceAxis() {
      let modifiedOpts = cloneDeep(this.opts);
      var axises = this.isVertical ? modifiedOpts.xAxis : modifiedOpts.yAxis;

      for (var axis of axises) {
        let totalName = "";

        for (var sltId of axis.slotIds) {
          let slt = this.widget?.slots.find((x) => x.slotId == sltId);
          if (slt) {
            let name = this.getAxisName(sltId, this.getSeriesName(slt));
            totalName += totalName.length > 0 ? ", " + name : name;
          }
        }
        if (totalName.length > this.allowedAxisNameLength)
          totalName =
            totalName.substring(0, this.allowedAxisNameLength) + "...";

        axis.name = totalName;
      }
      this.opts = modifiedOpts;
    },

    async updateDataZoom(event) {
      if (this.debounceTimer != null) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        const startProcent = event?.batch?.at(0)?.start ?? 0;
        const endProcent = event?.batch?.at(0)?.end ?? 100;
        this.dataZoomProcent = { start: startProcent, end: endProcent };

        if (
          this.shouldSyncDataZoom &&
          isEqual(this.dataZoomProcent, this.dashboardGlobalZoom) === false
        )
          this.SET_DASHBOARD_ZOOM(this.dataZoomProcent);

        this.getZoomedData(true);
        clearTimeout(this.debounceTimer);
      }, 200);
    },

    getZoomedData(emit) {
      const seriesCopy = cloneDeep(this.opts.series);
      for (var serie of seriesCopy)
        serie.data = this.calcZoomedData(
          this.seriesData[serie.slotId],
          this.dataZoomProcent,
          this.dateRange
        );
      if (emit) this.$emit("updated-chart-data", seriesCopy);
      return seriesCopy;
    },

    initAggregation() {
      // Set defaults for aggregation options
      this.aggregationSettings = this.setDefaultSeriesAggregation(
        this.opts?.series ?? []
      );

      // If aggregationSettingsInput is provided, replace slots in aggregationSettings that exists in aggregationSettingsInput
      if (this.aggregationSettingsInput !== undefined)
        for (var entry of Object.entries(this.aggregationSettingsInput))
          this.aggregationSettings[entry.at(0)] = entry.at(1);
    },

    initDateRange() {
      let dr = { start: "-24h", end: "Now" };

      if (this.getMeta(this.metaKeys.DEFAULT_TIMESPAN) !== undefined) {
        dr = { start: this.getFromDateOrDefault(), end: new Date() };
      }

      if (this.getMeta(this.metaKeys.DEFAULT_TIMERANGE)?.value !== undefined) {
        try {
          dr = JSON.parse(this.getMeta(this.metaKeys.DEFAULT_TIMERANGE).value);
        } catch (e) {
          console.error(e);
        }
      }

      if (this.dateRangeInput !== undefined) dr = this.dateRangeInput;

      if (
        this.shouldUseDashboardGlobalTimerange &&
        this.dashboardGlobalTime?.start != undefined &&
        this.dashboardGlobalTime?.end != undefined
      )
        dr = this.dashboardGlobalTime;

      this.dateRange = dr;
    },
  },

  async created() {
    if (this.shouldFake) this.fakeData();
    this.setupOptions();

    this.initAggregation();
    this.initDateRange();

    this.connectDataToSeries();
    this.widgetClone = cloneDeep(this.widget);
  },

  watch: {
    widget: {
      async handler(val) {
        if (!this.shouldFake && isEqual(this.widgetClone, val) === false) {
          await this.fetchData();
          if (this.isNewKeys()) {
            this.replaceLegend();
            this.replaceAxis();
          }
          this.connectDataToSeries();
          this.widgetClone = cloneDeep(val);
        }
      },
      deep: true,
    },

    dashboardGlobalTime: {
      async handler(newVal) {
        if (!this.shouldFake && this.shouldUseDashboardGlobalTimerange) {
          this.dateRange = newVal;
        }
      },
      deep: true,
    },

    dateRange: {
      async handler(newVal) {
        if (
          !this.shouldFake &&
          newVal !== undefined &&
          newVal?.start !== undefined &&
          newVal?.end !== undefined
        ) {
          // Here we can set a boolean to override if we should use the global timerange
          // We might want to allow the timespan changing in the chart while still using
          // the global timerange. But for now this isn't possible.
          await this.fetchData();
          this.connectDataToSeries();

          this.$emit("update-dateRange", newVal);
        }
      },
      deep: true,
    },

    aggregationSettingsInput: {
      handler() {
        this.initAggregation();
      },
      deep: true,
    },

    dateRangeInput: {
      handler() {
        this.initDateRange();
      },
      deep: true,
    },

    dashboardGlobalZoom: {
      async handler(val) {
        if (
          !this.shouldFake &&
          this.shouldSyncDataZoom &&
          isEqual(this.dataZoomProcent, val) === false
        ) {
          const modifiedOpts = cloneDeep(this.opts);
          const dz = modifiedOpts.dataZoom.find((x) => x.type == "inside");
          if (dz === undefined) {
            console.error("Could not find dataZoom inside");
            return;
          }

          if (val?.start !== undefined) dz.start = val.start;
          if (val?.end !== undefined) dz.end = val.end;

          this.dataZoomProcent = {
            start: dz.start,
            end: dz.end,
          };

          this.getZoomedData(true);
          this.opts = modifiedOpts;
        }
      },
      deep: true,
    },

    tags: {
      async handler(newVal, oldVal) {
        if (oldVal != newVal && newVal.length > 0) {
          await this.fetchData();
          this.replaceLegend();
          this.connectDataToSeries();
        }
      },
    },
  },
};
</script>
